import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { RecordingLoadingPage } from './RecordingLoadingPage';
import { RecordingPage } from './RecordingPage';
import { theme } from '../styles/theme';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ErrorContainer = styled.div`
  ${theme.glassContainer.normal}
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  padding: 32px;
  text-align: center;
  font-family: ${theme.typography.fontFamily};
`;

const ErrorTitle = styled.h2`
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.semibold};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ErrorMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.sizes.base};
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const BackButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
`;

interface SharedRecordingViewProps {
  callId: string;
  onNavigateHome: () => void;
}

export const SharedRecordingView: React.FC<SharedRecordingViewProps> = ({
  callId,
  onNavigateHome
}) => {
  const [recordingState, setRecordingState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [recordingData, setRecordingData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRecordingReady = (url: string, data: any) => {
    setRecordingUrl(url);
    setRecordingData(data);
    setRecordingState('ready');
  };

  const handleRecordingError = (errorMsg: string) => {
    setError(errorMsg);
    setRecordingState('error');
  };

  const handleNewCall = () => {
    window.location.hash = '';
    onNavigateHome();
  };

  if (recordingState === 'loading') {
    return (
      <Container>
        <RecordingLoadingPage
          callId={callId}
          onRecordingReady={handleRecordingReady}
          onError={handleRecordingError}
        />
      </Container>
    );
  }

  if (recordingState === 'error') {
    return (
      <Container>
        <ErrorContainer>
          <ErrorTitle>
            <AlertCircle size={24} />
            Recording Not Available
          </ErrorTitle>
          <ErrorMessage>
            {error || 'This recording could not be found or is no longer available. It may have expired or the link is invalid.'}
          </ErrorMessage>
          <BackButton onClick={handleNewCall}>
            <ArrowLeft size={16} />
            Create Your Own Podcast
          </BackButton>
        </ErrorContainer>
      </Container>
    );
  }

  if (recordingState === 'ready' && recordingUrl && recordingData) {
    return (
      <Container>
        <RecordingPage
          recordingUrl={recordingUrl}
          callData={recordingData}
          onNewCall={handleNewCall}
        />
      </Container>
    );
  }

  return null;
};
